import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, InputNumber, Select, Input, Button, Table, Tag, Space, Popconfirm, Empty, Modal, Alert } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import { ScreenHeader, QuantityStep, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import MachineSelector from '../../components/machine/MachineSelector';
import JustificationModal, { JustificationInput, isJustificationValid } from '../../components/modals/JustificationModal';

const fmtL = (v) => `${Number(v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} L`;

/**
 * Painel de Leitura do Tanque: o operador lê o QR da máquina e informa o valor
 * TOTAL que está no tanque naquele momento (nunca a diferença).
 *
 * Proteções contra apontamento errado (regra do cliente):
 * - Antes de salvar, o modal de confirmação mostra o nível que o sistema
 *   esperava e a transferência máquina -> tanque que a leitura vai gerar.
 * - Leituras suspeitas (menores que o esperado ou com transferência maior que
 *   o saldo da máquina) exigem REDIGITAR o valor para confirmar.
 * - Correção pós-gravação: Admin sem restrição (justificativa opcional);
 *   Operador-comprador e Líder corrigem/excluem leituras DE HOJE com
 *   justificativa obrigatória. Toda correção recalcula as transferências do
 *   dia no back-end.
 *
 * Prop `mobile` (opcional): quando true (operadores no PWA), UI vertical com
 * cards do dia; quando false (Admin), tabela com editar/excluir e filtro.
 */
const TanquePanel = ({ mobile: mobileProp }) => {
  const { loggedUser } = useContext(GeneralContext);
  const location = useLocation();
  const isAdmin = loggedUser?.type === EUserTypes.PANEL_ADMIN.code;
  const mobile = mobileProp !== undefined ? mobileProp : !isAdmin;
  const flowColor = location.state?.color || FLOW_COLORS.tanque;
  // Comprador/líder (e admin) podem corrigir leituras de hoje pelo PWA.
  const canCorrect = EUserTypes.canEditToday(loggedUser);
  // Justificativa obrigatória para não-admin (validada também na API).
  const justRequired = !isAdmin;

  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  // Correção no PWA: modal de edição (campos + justificativa) e alvo de exclusão.
  const [editModal, setEditModal] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Máquina da sessão atual (em memória), escolhida via QR ao abrir o painel.
  const [machine, setMachine] = useState(null);
  // Lista de máquinas para o filtro/select do admin.
  const [machines, setMachines] = useState([]);
  const [machineFilter, setMachineFilter] = useState(undefined);

  // Modal de confirmação com a variação calculada (proteção contra erro).
  // `suspicious` = leitura menor que o esperado ou transferência implausível:
  // nesses casos o operador precisa redigitar o valor para poder confirmar.
  const [confirmState, setConfirmState] = useState(null); // { values, preview }
  const [retypeValue, setRetypeValue] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (mobile) {
        const res = await api.operation.listTankReadingsToday();
        setList(res.data?.rows || []);
      } else {
        const params = { page: 1, pageSize: 50 };
        if (machineFilter) params.machineId = machineFilter;
        const res = await api.operation.listTankReadings(params);
        setList(res.data?.rows || []);
      }
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar leituras do tanque.');
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const res = await api.machine.list({ page: 1, pageSize: 100 });
      setMachines(res.data?.rows || []);
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { load(); loadMachines(); }, []);
  useEffect(() => { if (!mobile) load(); }, [machineFilter]);

  /**
   * Passo 1 do registro: busca a pré-visualização (nível esperado + variação)
   * e abre o modal de confirmação. Nada é gravado aqui.
   */
  const handleAdd = async (values) => {
    if (!machine?.id) {
      showErrorMessage('Selecione a máquina do tanque.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.operation.previewTankReading({ machine_id: machine.id, liters: values.liters });
      setRetypeValue(null);
      setConfirmState({ values, preview: res.data });
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao calcular a leitura do tanque.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Passo 2: confirmação do operador no modal — grava de fato.
   */
  const handleConfirmSave = async () => {
    if (!confirmState) return;
    setSaving(true);
    try {
      await api.operation.createTankReading({ ...confirmState.values, machine_id: machine.id });
      showSuccessMessage('Leitura do tanque registrada!');
      setConfirmState(null);
      form.resetFields();
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao registrar leitura do tanque.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.operation.updateTankReading(id, editRecord);
      showSuccessMessage('Leitura atualizada! As transferências do dia foram recalculadas.');
      setEditingId(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao atualizar leitura.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.operation.deleteTankReading(id);
      showSuccessMessage('Leitura removida! As transferências do dia foram recalculadas.');
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover leitura.');
    }
  };

  /** Salva a edição feita no modal do PWA (campos + justificativa). */
  const handleMobileUpdate = async () => {
    if (!editModal) return;
    setSavingEdit(true);
    try {
      await api.operation.updateTankReading(editModal.id, { ...editRecord, justification: editJustification.trim() || undefined });
      showSuccessMessage('Leitura corrigida! As transferências do dia foram recalculadas.');
      setEditModal(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao corrigir leitura.');
    } finally {
      setSavingEdit(false);
    }
  };

  /** Exclui via modal de justificativa (PWA). */
  const handleMobileDelete = async (justification) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.operation.deleteTankReading(deleteTarget.id, { justification: justification || undefined });
      showSuccessMessage('Leitura removida! As transferências do dia foram recalculadas.');
      setDeleteTarget(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover leitura.');
    } finally {
      setDeleting(false);
    }
  };

  /** Abre o modal de correção com os valores atuais da leitura. */
  const openEditModal = (r) => {
    setEditRecord({ liters: Number(r.liters), observation: r.observation });
    setEditJustification('');
    setEditModal(r);
  };

  const preview = confirmState?.preview;
  const informedLiters = confirmState?.values?.liters;
  // Leitura suspeita: menor que o esperado (variação negativa) ou transferindo
  // mais do que a máquina tem registrado (implausível).
  const suspicious = preview ? (preview.variation < 0 || preview.implausible) : false;
  const retypeMatches = retypeValue != null && Number(retypeValue) === Number(informedLiters);

  const confirmModal = (
    <Modal
      title={suspicious ? (<span><WarningOutlined style={{ color: '#d97706', marginRight: 8 }} />Leitura suspeita — confira o valor</span>) : 'Confirmar leitura do tanque'}
      open={!!confirmState}
      onCancel={() => setConfirmState(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setConfirmState(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          danger={suspicious}
          loading={saving}
          disabled={suspicious && !retypeMatches}
          onClick={handleConfirmSave}
        >
          {suspicious ? 'Confirmar mesmo assim' : 'Confirmar leitura'}
        </Button>,
      ]}
    >
      {preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Você informou que o tanque tem</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: flowColor }}>{fmtL(informedLiters)}</div>
          </div>

          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 14 }}>
            <div>O sistema esperava <strong>{fmtL(preview.estimatedLevel)}</strong> no tanque.</div>
            {preview.variation >= 0 ? (
              <div style={{ marginTop: 4 }}>
                Isso significa que <strong>{fmtL(preview.variation)}</strong> saíram da máquina para o tanque.
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                A leitura é <strong>{fmtL(Math.abs(preview.variation))} MENOR</strong> que o esperado.
              </div>
            )}
          </div>

          {preview.variation < 0 && (
            <Alert
              type="warning"
              showIcon
              message="Leitura menor que o esperado"
              description="O tanque não perde líquido sem descarga registrada. Isso normalmente indica erro de digitação ou descarga não apontada."
            />
          )}
          {preview.implausible && (
            <Alert
              type="warning"
              showIcon
              message="Transferência maior que o saldo da máquina"
              description={`A leitura indica que ${fmtL(preview.variation)} saíram da máquina, mas ela tem apenas ${fmtL(preview.machineBalance)} registrados hoje. Verifique o valor digitado (ou se falta apontamento de RC/processamento).`}
            />
          )}

          {suspicious && (
            <div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                Para confirmar mesmo assim, digite novamente o valor lido no tanque:
              </div>
              <InputNumber
                value={retypeValue}
                onChange={setRetypeValue}
                min={0}
                step={0.1}
                decimalSeparator=","
                style={{ width: '100%' }}
                size="large"
                placeholder={`Redigite ${informedLiters}`}
                aria-label="Redigite o valor lido no tanque para confirmar"
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );

  const columns = [
    { title: 'Data/hora', dataIndex: 'occurred_at', key: 'occurred_at', render: v => new Date(v).toLocaleString('pt-BR') },
    { title: 'Máquina', key: 'machine', render: (_, r) => (r.machine ? `${r.machine.code}` : '—') },
    {
      title: 'Leitura (L)', key: 'liters',
      render: (_, r) => editingId === r.id ? (
        <InputNumber value={editRecord?.liters} onChange={v => setEditRecord(p => ({ ...p, liters: v }))} min={0} step={0.1} decimalSeparator="," />
      ) : fmtL(r.liters)
    },
    {
      title: 'Transferência (L)', key: 'variation_liters',
      render: (_, r) => {
        const v = Number(r.variation_liters ?? 0);
        return <Tag color={v < 0 ? 'red' : 'blue'}>{v >= 0 ? '+' : ''}{v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} L</Tag>;
      }
    },
    {
      // Rastreabilidade: leitura registrada dentro do fluxo de descarga
      // (valor ANTES da descarga) ou avulsa pelo menu Tanque.
      title: 'Origem', key: 'origin',
      render: (_, r) => r.drainage_id
        ? <Tag color="purple">Via descarga</Tag>
        : <Tag>Avulsa</Tag>
    },
    {
      title: 'Obs.', key: 'observation',
      render: (_, r) => editingId === r.id ? (
        <Input value={editRecord?.observation || ''} onChange={e => setEditRecord(p => ({ ...p, observation: e.target.value }))} />
      ) : (r.observation || '—')
    },
    { title: 'Usuário', key: 'user', render: (_, r) => r.user?.full_name || '—' },
    {
      title: 'Ações', key: 'actions',
      render: (_, r) => {
        // Correção pós-gravação é exclusiva do Admin (decisão do cliente).
        if (!isAdmin) return null;
        if (editingId === r.id) {
          return (
            <Space>
              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => handleUpdate(r.id)} />
              <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
            </Space>
          );
        }
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); setEditRecord({ liters: Number(r.liters), observation: r.observation }); }}>Editar</Button>
            <Popconfirm
              title="Excluir esta leitura? As transferências do dia serão recalculadas."
              okText="Excluir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  // Modal de correção do PWA (comprador/líder/admin): campos + justificativa.
  // A edição recalcula automaticamente as transferências do dia no back-end.
  const mobileEditModal = (
    <Modal
      title="Corrigir leitura do tanque"
      open={!!editModal}
      onCancel={() => setEditModal(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setEditModal(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          loading={savingEdit}
          disabled={!isJustificationValid(editJustification, justRequired)}
          onClick={handleMobileUpdate}
        >
          Salvar correção
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Alert
          type="info"
          showIcon
          message="Ao corrigir esta leitura, as transferências do dia da máquina serão recalculadas automaticamente."
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Total no tanque (L)</div>
          <InputNumber
            value={editRecord?.liters}
            onChange={(v) => setEditRecord((p) => ({ ...p, liters: v }))}
            min={0}
            step={0.1}
            decimalSeparator=","
            style={{ width: '100%' }}
            size="large"
            aria-label="Total no tanque em litros"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Observação</div>
          <Input.TextArea
            value={editRecord?.observation || ''}
            onChange={(e) => setEditRecord((p) => ({ ...p, observation: e.target.value }))}
            autoSize={{ minRows: 2 }}
            aria-label="Observação da leitura"
          />
        </div>
        <JustificationInput value={editJustification} onChange={setEditJustification} required={justRequired} />
      </div>
    </Modal>
  );

  const mobileDeleteModal = (
    <JustificationModal
      open={!!deleteTarget}
      title="Excluir leitura do tanque"
      description={deleteTarget ? (
        <span>
          Excluir a leitura de <strong>{fmtL(deleteTarget.liters)}</strong>
          {deleteTarget.machine ? <> da máquina <strong>{deleteTarget.machine.code}</strong></> : null}?
          {' '}As transferências do dia serão recalculadas.
        </span>
      ) : null}
      confirmLabel="Excluir"
      danger
      loading={deleting}
      required={justRequired}
      onConfirm={handleMobileDelete}
      onCancel={() => setDeleteTarget(null)}
    />
  );

  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackToHome color={flowColor} />
        <Card loading={loading}>
          <ScreenHeader
            title="Leitura do tanque"
            subtitle="Leia o QR da máquina e informe o TOTAL que está no tanque agora (não a diferença)."
            color={flowColor}
          />
          <Form form={form} layout="vertical" onFinish={handleAdd}>
            <div style={{ marginBottom: 16 }}>
              <MachineSelector value={machine} onChange={setMachine} required color={flowColor} />
            </div>
            <QuantityStep
              form={form}
              name="liters"
              label="Total no tanque (L)"
              hint="Quanto tem no tanque neste momento, em litros."
            />
            <Form.Item label="Observação" name="observation">
              <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Opcional" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving} size="large" block style={primaryButtonStyle(flowColor)}>
                Registrar leitura
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card loading={loading}>
          <ScreenHeader title="Leituras de hoje" subtitle="Registros do dia atual." color={flowColor} />
          {list.length === 0 ? (
            <Empty description="Nenhuma leitura registrada hoje." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {list.map((r) => {
                const variation = Number(r.variation_liters ?? 0);
                return (
                  <div
                    key={r.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      background: '#fafbff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        <Tag color={variation < 0 ? 'red' : 'blue'}>
                          Transf. {variation >= 0 ? '+' : ''}{variation.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} L
                        </Tag>
                        {r.drainage_id ? <Tag color="purple">Via descarga</Tag> : null}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ fontSize: 18 }}>{fmtL(r.liters)}</strong>
                        {canCorrect && (
                          <Space size={4}>
                            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(r)} aria-label="Corrigir leitura do tanque" />
                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setDeleteTarget(r)} aria-label="Excluir leitura do tanque" />
                          </Space>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {r.machine ? `${r.machine.code} · ` : ''}
                      {new Date(r.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {r.observation ? ` · ${r.observation}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {confirmModal}
        {mobileEditModal}
        {mobileDeleteModal}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Registrar leitura do tanque" size="small">
        <Form form={form} layout="inline" onFinish={handleAdd}>
          <Form.Item label="Máquina" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecione a máquina"
              style={{ width: 220 }}
              value={machine?.id}
              onChange={(v) => {
                const m = machines.find((x) => x.id === v);
                setMachine(m ? { id: m.id, code: m.code, name: m.name } : null);
              }}
              options={machines.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
              aria-label="Máquina da leitura do tanque"
            />
          </Form.Item>
          <Form.Item label="Total no tanque (L)" name="liters" rules={[{ required: true, message: 'Informe o total no tanque.' }]}>
            <InputNumber min={0} step={0.1} decimalSeparator="," style={{ width: 160 }} />
          </Form.Item>
          <Form.Item label="Observação" name="observation">
            <Input style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Registrar</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Leituras recentes" size="small"
        extra={
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filtrar por máquina"
            style={{ width: 200 }}
            value={machineFilter}
            onChange={setMachineFilter}
            options={machines.map(m => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
          />
        }
      >
        <Table rowKey="id" size="small" loading={loading} dataSource={list} columns={columns} pagination={{ pageSize: 10 }} />
      </Card>

      {confirmModal}
    </div>
  );
};

export default TanquePanel;
